import { create } from "zustand";

// -----------------------
// Basic Info Types
// -----------------------

export interface BasicInfoState {
  designNo: string;
  clientName: string;
  colorSpecification: string;
  designCategory: string;
  status: string;
}

// -----------------------
// Fabric Specs Types
// -----------------------
export interface FabricSpecsState {
  fabricType: string | null;
  chartName: string;
  chartNo: string;
  vendorDetails: string | null;
  billNoWithDate: string;
  panna: string;
  pannaRate: string;
  agentName: string;
  discountDetails: string;
  masterRecordRef: string;
  fabricNotes: string;
}

// -----------------------
// Astar Types
// -----------------------
export interface AstarState {
  astarType: string | null;
  chartName: string;
  chartNo: string;
  vendorDetails: string | null;
  billNoWithDate: string;
  panna: string;
  pannaRate: string;
  agentName: string;
  discountDetails: string;
  masterRecordRef: string;
  astarNotes: string;
}

// -----------------------
// bupatta Types
// -----------------------
export interface DupattaState {
  dupattaType: string;
  chartName?: string;
  chartNo?: string;
  vendorDetails: string | null;
  billNoWithDate: string;
  panna: string;
  pannaRate: string;
  agentName: string;
  discountDetails: string;
  masterRecordRef: string;
  dupattaNotes: string;
}

// -----------------------
// AdditionalFabricState Types
// -----------------------
export interface AdditionalFabricState {
  fabricType: string;
  chartName: string;
  chartNo: string;
  vendorDetails: string | null;
  billNoWithDate: string;
  panna: string;
  pannaRate: string;
  agentName: string;
  discountDetails: string;
  masterRecordRef: string;
  fabricNotes: string;
}

// -----------------------
// ImageState Types
// -----------------------
export interface ImageDetails {
  url: string;
  objectName: string;
  originalName: string;
  expiresAt: string;
}
// export interface ImageState {
//   primaryImage: File | string | null;
//   additionalImage1: File | string | null;
//   additionalImage2: File | string | null;
//   additionalImage3: File | string | null;
//   additionalImage4: File | string | null;
// }

export interface ImageState {
  primaryImage: ImageDetails | File | null;
  additionalImage1: ImageDetails | File | null;
  additionalImage2: ImageDetails | File | null;
  additionalImage3: ImageDetails | File | null;
  additionalImage4: ImageDetails | File | null;
}

//
// ------ FABRIC USAGE  -----
//

export interface SizeSegment {
  id: string;
  name: string;
}
export interface FabricUsageRow {
  id: string;
  component: string;
  values: { [sizeId: string]: string };
  vendorId?: string;
  isPredefined?: boolean;
}

export interface FabricUsageCategory {
  rows: FabricUsageRow[];
  selection?: string | null;
  options?: { id: string; label: string }[];
}
export interface NotesState {
  specialCuttingInstructions: string;
  notesAndRemarks: string;
}

export interface FabricUsageState {
  sizeSegments: SizeSegment[];

  fabricComponents: FabricUsageCategory;
  designWork: FabricUsageCategory;
  astar: FabricUsageCategory;
  dupatta: FabricUsageCategory;
  rate: FabricUsageCategory;
  handwork: FabricUsageCategory;
  embroidery: FabricUsageCategory;
  manualEmbroidery: FabricUsageCategory;
  stitching: FabricUsageCategory;
  accessories: FabricUsageCategory;
  additionalComponent: FabricUsageCategory;
}

//
// ------ overall costing  -----
//

// export interface CostingBreakdown {
//   [sizeId: string]: {
//     fabricComponentsTotal: number;
//     designWorkTotal: number;
//     designWorkUsage: number;
//     astarTotal: number;
//     dupattaTotal: number;
//     additionalComponent: number;

//     fabricComponentsCost: number;
//     designWorkCost: number;
//     astarCost: number;
//     dupattaCost: number;
//     additionalComponentCost: number;
//     handWorkTotal: number;
//     embroideryTotal: number;
//     stitchingTotal: number;
//     accessoriesTotal: number;
//     additionalComponentTotal: number;
//     materialCost: number;
//     directRatesTotal: number;
//     rateTableTotal: number;
//     finalPrice: number;
//   };
// }

export interface CostingBreakdown {
  [sizeId: string]: {
    // Usage (in meters)
    fabricComponentsUsage: number;
    designWorkUsage: number;
    astarUsage: number;
    dupattaUsage: number;
    additionalComponentUsage: number;
    manualEmbroideryUsage: number;
    // Costs (Usage * Rate)
    fabricComponentsCost: number;
    designWorkCost: number;
    astarCost: number;
    dupattaCost: number;
    additionalComponentCost: number;
    manualEmbroideryCost: number;
    // Direct Rates
    handWorkTotal: number;
    embroideryTotal: number;
    stitchingTotal: number;
    accessoriesTotal: number;
    rateTableTotal: number;
    // Final Totals
    totalMaterialCost: number;
    totalDirectRates: number;
    finalPrice: number;
  };
}

export interface OverallCostingState {
  costingNotes: string;
  breakdown: CostingBreakdown;
}

interface DesignFormState {
  basicInfo: BasicInfoState;
  fabricSpecs: FabricSpecsState;
  astar: AstarState;
  dupatta: DupattaState;
  additionalFabric: AdditionalFabricState;
  images: ImageState;
  fabricUsage: FabricUsageState;
  notes: NotesState;
  overallCosting: OverallCostingState;
}

type FabricUsageCategoryKey = keyof Omit<FabricUsageState, "sizeSegments">;

interface DesignFormActions {
  updateBasicInfo: (field: keyof BasicInfoState, value: string) => void;
  updateAstar: (field: keyof AstarState, value: string) => void;
  setAstarSpecs: (newSpecs: Partial<AstarState>) => void;
  setFabricSpecs: (newSpecs: Partial<FabricSpecsState>) => void;
  updateFabricSpecs: (field: keyof FabricSpecsState, value: string) => void;
  updateDupatta: (field: keyof DupattaState, value: string) => void;
  updateAdditionalFabric: (
    field: keyof AdditionalFabricState,
    value: string
  ) => void;
  setSizeSegments: (segments: SizeSegment[]) => void;
  addFabricUsageRow: (category: FabricUsageCategoryKey) => void;
  updateFabricUsageRow: (
    category: FabricUsageCategoryKey,
    rowIndex: number,
    field: "component" | string,
    value: string
  ) => void;
  removeFabricUsageRow: (
    category: FabricUsageCategoryKey,
    rowIndex: number
  ) => void;

  setCategorySelection: (
    category: FabricUsageCategoryKey,
    value: string | null
  ) => void;
  setCategoryOptions: (
    category: FabricUsageCategoryKey,
    options: { id: string; label: string }[]
  ) => void;
  setFormData: (data: Partial<DesignFormState>) => void;
  updateNotes: (field: keyof NotesState, value: string) => void;
  updateCostingBreakdown: (breakdown: CostingBreakdown) => void;
  updateOverallCostingNotes: (notes: string) => void;
  setPrimaryImage: (file: File | null) => void;
  updateAdditionalImage: (index: number, file: File | string | null) => void;
  getFormData: () => DesignFormState;
  resetBasicInfo: () => void;
  resetFabricSpecs: () => void;
  resetAstar: () => void;
  resetImages: () => void;
  resetDupatta: () => void;
  resetfabricUsage: () => void;
  resetAdditionalFabric: () => void;
  resetAll: () => void;

  validateForm: () => { valid: boolean; missingFields: string[] };
}

const initialBasicInfo: BasicInfoState = {
  designNo: "",
  clientName: "",
  colorSpecification: "",
  designCategory: "",
  status: "Active",
};

const initialFabricSpecs: FabricSpecsState = {
  fabricType: null,
  chartName: "",
  chartNo: "",
  vendorDetails: null,
  billNoWithDate: "",
  panna: "",
  pannaRate: "0.00",
  agentName: "",
  discountDetails: "",
  masterRecordRef: "",
  fabricNotes: "",
};

const initialOverallCosting: OverallCostingState = {
  costingNotes: "",
  breakdown: {},
};

const initialAstar: AstarState = {
  astarType: null,
  chartName: "",
  chartNo: "",
  vendorDetails: null,
  billNoWithDate: "",
  panna: "",
  pannaRate: "",
  agentName: "",
  discountDetails: "",
  masterRecordRef: "",
  astarNotes: "",
};

const initialDupatta: DupattaState = {
  dupattaType: "",
  chartName: "",
  chartNo: "",
  vendorDetails: null,
  billNoWithDate: "",
  panna: "",
  pannaRate: "",
  agentName: "",
  discountDetails: "",
  masterRecordRef: "",
  dupattaNotes: "",
};

const initialAdditionalFabric: AdditionalFabricState = {
  fabricType: "",
  chartName: "",
  chartNo: "",
  vendorDetails: null,
  billNoWithDate: "",
  panna: "",
  pannaRate: "",
  agentName: "",
  discountDetails: "",
  masterRecordRef: "",
  fabricNotes: "",
};

// const initialFabricUsage: FabricUsageState = {
//     sizeSegments: [],
//     fabricComponents: { rows: [] },
//     designWork: { rows: [], selection: null, options: [] },
//     rate: { rows: [] },
//     handwork: { rows: [], selection: null, options: [] },
//     embroidery: { rows: [], selection: null, options: [] },
//     stitching: { rows: [], selection: null, options: [] },
//     accessories: { rows: [], selection: null, options: [] },
// };

const initialFabricUsage = (): FabricUsageState => ({
  sizeSegments: [],
  fabricComponents: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Top",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Bottom",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Sleeves",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Dupatta",
        values: {},
        isPredefined: true,
      },
    ],
  },
  designWork: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Embroidery",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  astar: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Astar",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },

  dupatta: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Dupatta",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  rate: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Top",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Sleeves",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Bottom",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "All over",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Daman Lace",
        values: {},
        isPredefined: true,
      },
      {
        id: crypto.randomUUID(),
        component: "Other Lace",
        values: {},
        isPredefined: true,
      },
    ],
  },
  handwork: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Handwork",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  embroidery: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Embroidery",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  manualEmbroidery: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Manual Embroidery",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  stitching: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Stitching",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  accessories: {
    rows: [
      {
        id: crypto.randomUUID(),
        component: "Accessories",
        values: {},
        isPredefined: true,
      },
    ],
    selection: null,
    options: [],
  },
  additionalComponent: {
    rows: [],
    selection: null,
    options: [],
  },
});

const initialNotes: NotesState = {
  specialCuttingInstructions: "",
  notesAndRemarks: "",
};

// const initialImages: ImageState = {
//     primaryImage: null,
//     additionalImages: [null, null, null, null],
// };
const initialImages: ImageState = {
  primaryImage: null,
  additionalImage1: null,
  additionalImage2: null,
  additionalImage3: null,
  additionalImage4: null,
};

// -----------------------
// Required fields config
// -----------------------
const requiredFields = {
  basicInfo: ["designNo", "clientName", "colorSpecification", "designCategory"],
  fabricSpecs: ["fabricType", "chartName", "chartNo"],
  astar: ["astarType", "chartName", "chartNo"],
  dupatta: ["dupattaType"],
  additionalFabric: ["fabricType"],
  images: ["primaryImage"],
};
const sectionToTabMap: Record<string, string> = {
  basicInfo: "Basic Info",
  fabricSpecs: "Fabric Specs",
  astar: "Astar",
  dupatta: "Dupatta",
  additionalFabric: "Additional Fabric",
  images: "Images",
};

export const useDesignFormStore = create<DesignFormState & DesignFormActions>(
  (set, get) => ({
    basicInfo: initialBasicInfo,
    fabricSpecs: initialFabricSpecs,
    astar: initialAstar,
    dupatta: initialDupatta,
    additionalFabric: initialAdditionalFabric,
    images: initialImages,
    fabricUsage: initialFabricUsage(),
    notes: initialNotes,
    overallCosting: initialOverallCosting,

    updateBasicInfo: (field, value) =>
      set((state) => ({
        basicInfo: {
          ...state.basicInfo,
          [field]: value,
        },
      })),

    updateFabricSpecs: (field, value) =>
      set((state) => ({
        fabricSpecs: {
          ...state.fabricSpecs,
          [field]: value,
        },
      })),

    setFabricSpecs: (newSpecs) =>
      set((state) => ({
        fabricSpecs: { ...state.fabricSpecs, ...newSpecs },
      })),

    updateAstar: (field, value) =>
      set((state) => ({
        astar: { ...state.astar, [field]: value },
      })),

    setAstarSpecs: (newSpecs) =>
      set((state) => ({
        astar: { ...state.astar, ...newSpecs },
      })),

    updateDupatta: (field, value) =>
      set((state) => ({
        dupatta: { ...state.dupatta, [field]: value },
      })),

    updateAdditionalFabric: (field, value) =>
      set((state) => ({
        additionalFabric: { ...state.additionalFabric, [field]: value },
      })),

    setPrimaryImage: (file) =>
      set((state) => ({
        images: { ...state.images, primaryImage: file },
      })),

    // NEW: A simple action to update one additional image at a time.
    updateAdditionalImage: (index, file) =>
      set((state) => ({
        images: { ...state.images, [`additionalImage${index}`]: file },
      })),

    setSizeSegments: (segments) =>
      set((state) => ({
        fabricUsage: { ...state.fabricUsage, sizeSegments: segments },
      })),

    addFabricUsageRow: (category) =>
      set((state) => {
        const newRow: FabricUsageRow = {
          id: crypto.randomUUID(),
          component: "",
          values: {},
        };
        return {
          fabricUsage: {
            ...state.fabricUsage,
            [category]: {
              ...state.fabricUsage[category],
              rows: [...state.fabricUsage[category].rows, newRow],
            },
          },
        };
      }),

    updateFabricUsageRow: (category, rowIndex, field, value) =>
      set((state) => {
        const categoryState = state.fabricUsage[category];
        const updatedRows = [...categoryState.rows];
        const rowToUpdate = { ...updatedRows[rowIndex] };

        if (field === "component") {
          rowToUpdate.component = value;
        } else {
          rowToUpdate.values = { ...rowToUpdate.values, [field]: value };
        }

        updatedRows[rowIndex] = rowToUpdate;

        return {
          fabricUsage: {
            ...state.fabricUsage,
            [category]: { ...categoryState, rows: updatedRows },
          },
        };
      }),

    removeFabricUsageRow: (category, rowIndex) =>
      set((state) => {
        const categoryState = state.fabricUsage[category];
        const updatedRows = categoryState.rows.filter(
          (_, index) => index !== rowIndex
        );
        return {
          fabricUsage: {
            ...state.fabricUsage,
            [category]: { ...categoryState, rows: updatedRows },
          },
        };
      }),

    setFormData: (data) =>
      set((state) => {
        const normalizeId = (fieldData: any) => {
          if (fieldData && typeof fieldData === "object" && fieldData._id) {
            return fieldData._id;
          }
          return fieldData;
        };

        const newFabricSpecs: Partial<FabricSpecsState> = data.fabricSpecs
          ? { ...data.fabricSpecs }
          : {};

        if (newFabricSpecs.vendorDetails) {
          newFabricSpecs.vendorDetails = normalizeId(
            newFabricSpecs.vendorDetails
          );
        }
        if (newFabricSpecs.fabricType) {
          newFabricSpecs.fabricType = normalizeId(newFabricSpecs.fabricType);
        }

        const newAstar: Partial<AstarState> = data.astar
          ? { ...data.astar }
          : {};
        if (newAstar.vendorDetails) {
          newAstar.vendorDetails = normalizeId(newAstar.vendorDetails);
        }
        if (newAstar.astarType) {
          newAstar.astarType = normalizeId(newAstar.astarType);
        }

        const newDupatta: Partial<DupattaState> = data.dupatta
          ? { ...data.dupatta }
          : {};
        if (newDupatta.vendorDetails) {
          newDupatta.vendorDetails = normalizeId(newDupatta.vendorDetails);
        }

        const newAdditionalFabric: Partial<AdditionalFabricState> =
          data.additionalFabric ? { ...data.additionalFabric } : {};
        if (newAdditionalFabric.vendorDetails) {
          newAdditionalFabric.vendorDetails = normalizeId(
            newAdditionalFabric.vendorDetails
          );
        }

        const mergeRows = (defaultRows: any[], backendRows?: any[]) => {
          if (!backendRows || backendRows.length === 0) return defaultRows;
          if (defaultRows.every((row) => !row.isPredefined)) {
            return backendRows.map((row) => ({
              ...row,
              id: row.id || crypto.randomUUID(),
            }));
          }
          // For predefined rows, merge carefully
          const merged = [...defaultRows];
          backendRows.forEach((backendRow, index) => {
            if (merged[index]) {
              merged[index] = { ...merged[index], ...backendRow };
            } else {
              merged.push(backendRow);
            }
          });
          return merged;
        };
        const normalizeSelection = (selection: any) => {
          if (!selection) return null;
          return (
            selection?._id ||
            selection?.$oid ||
            selection?.id ||
            selection ||
            null
          );
        };
        const backendImageData = data.images as any;
        const backendImages = backendImageData?.additionalImages ?? [];
        const newImagesState: ImageState = {
          primaryImage: backendImageData?.primaryImage || null,
          additionalImage1: backendImages[0] || null,
          additionalImage2: backendImages[1] || null,
          additionalImage3: backendImages[2] || null,
          additionalImage4: backendImages[3] || null,
        };

        return {
          ...state,
          basicInfo: { ...state.basicInfo, ...data.basicInfo },

          fabricSpecs: { ...state.fabricSpecs, ...newFabricSpecs },
          astar: { ...state.astar, ...newAstar },
          dupatta: { ...state.dupatta, ...newDupatta },
          additionalFabric: {
            ...state.additionalFabric,
            ...newAdditionalFabric,
          },
          images: newImagesState,
          fabricUsage: {
            ...state.fabricUsage,
            fabricComponents: {
              ...state.fabricUsage.fabricComponents,
              rows: mergeRows(
                state.fabricUsage.fabricComponents.rows,
                data.fabricUsage?.fabricComponents?.rows
              ),
              selection: normalizeSelection(
                data.fabricUsage?.fabricComponents?.selection
              ),
            },
            designWork: {
              ...state.fabricUsage.designWork,
              // For designWork, if backend has rows, use them (existing design)
              // Otherwise use the new default with "Embroidery" (new design)
              rows:
                data.fabricUsage?.designWork?.rows &&
                data.fabricUsage.designWork.rows.length > 0
                  ? data.fabricUsage.designWork.rows.map((row: any) => ({
                      ...row,
                      id: row.id || crypto.randomUUID(),
                    }))
                  : state.fabricUsage.designWork.rows,
              selection: normalizeSelection(
                data.fabricUsage?.designWork?.selection
              ),
            },
            astar: {
              ...state.fabricUsage.astar,
              rows: mergeRows(
                state.fabricUsage.astar.rows,
                data.fabricUsage?.astar?.rows
              ),
              selection: normalizeSelection(data.fabricUsage?.astar?.selection),
            },
            dupatta: {
              ...state.fabricUsage.dupatta,
              rows: mergeRows(
                state.fabricUsage.dupatta.rows,
                data.fabricUsage?.dupatta?.rows
              ),
              selection: normalizeSelection(
                data.fabricUsage?.dupatta?.selection
              ),
            },
            rate: {
              ...state.fabricUsage.rate,
              rows: mergeRows(
                state.fabricUsage.rate.rows,
                data.fabricUsage?.rate?.rows
              ),
              selection: normalizeSelection(data.fabricUsage?.rate?.selection),
            },
            handwork: {
              ...state.fabricUsage.handwork,
              rows: mergeRows(
                state.fabricUsage.handwork.rows,
                data.fabricUsage?.handwork?.rows
              ),
              selection: normalizeSelection(
                data.fabricUsage?.handwork?.selection
              ),
            },
            embroidery: {
              ...state.fabricUsage.embroidery,
              // Filter out "Manual Embroidery" rows from embroidery - they go to manualEmbroidery
              rows: mergeRows(
                state.fabricUsage.embroidery.rows,
                data.fabricUsage?.embroidery?.rows?.filter(
                  (row: any) => row.component !== "Manual Embroidery"
                )
              ),
              selection: normalizeSelection(
                data.fabricUsage?.embroidery?.selection
              ),
            },
            manualEmbroidery: {
              ...state.fabricUsage.manualEmbroidery,
              // Map "Manual Embroidery" rows from backend embroidery to manualEmbroidery
              // Also check if backend has a separate manualEmbroidery field
              rows: (() => {
                const backendManualRows =
                  data.fabricUsage?.embroidery?.rows?.filter(
                    (row: any) => row.component === "Manual Embroidery"
                  ) || [];
                const backendManualEmbroideryRows =
                  data.fabricUsage?.manualEmbroidery?.rows || [];
                const allManualRows = [
                  ...backendManualRows,
                  ...backendManualEmbroideryRows,
                ];
                if (allManualRows.length > 0) {
                  return allManualRows.map((row: any) => ({
                    ...row,
                    id: row.id || crypto.randomUUID(),
                  }));
                }
                return state.fabricUsage.manualEmbroidery.rows;
              })(),
              selection: normalizeSelection(
                data.fabricUsage?.manualEmbroidery?.selection ||
                  data.fabricUsage?.embroidery?.selection
              ),
            },
            stitching: {
              ...state.fabricUsage.stitching,
              rows: mergeRows(
                state.fabricUsage.stitching.rows,
                data.fabricUsage?.stitching?.rows
              ),
              selection: normalizeSelection(
                data.fabricUsage?.stitching?.selection
              ),
            },
            accessories: {
              ...state.fabricUsage.accessories,
              rows: mergeRows(
                state.fabricUsage.accessories.rows,
                data.fabricUsage?.accessories?.rows
              ),
              selection: normalizeSelection(
                data.fabricUsage?.accessories?.selection
              ),
            },
            additionalComponent: {
              ...state.fabricUsage.additionalComponent,
              rows: mergeRows(
                state.fabricUsage.additionalComponent.rows,
                data.fabricUsage?.additionalComponent?.rows
              ),
              selection: normalizeSelection(
                data.fabricUsage?.additionalComponent?.selection
              ),
            }, // +++ ADD THIS LINE FOR MERGING +++
          },
          notes: { ...state.notes, ...data.notes },
          overallCosting: { ...state.overallCosting, ...data.overallCosting },
        };
      }),

    // --- NEW NOTES ACTION ---
    updateNotes: (field, value) =>
      set((state) => ({
        notes: { ...state.notes, [field]: value },
      })),
    updateCostingBreakdown: (breakdown) =>
      set((state) => ({
        overallCosting: { ...state.overallCosting, breakdown },
      })),

    updateOverallCostingNotes: (notes) =>
      set((state) => ({
        overallCosting: { ...state.overallCosting, costingNotes: notes },
      })),

    setCategorySelection: (category, value) =>
      set((state) => ({
        fabricUsage: {
          ...state.fabricUsage,
          [category]: { ...state.fabricUsage[category], selection: value },
        },
      })),

    setCategoryOptions: (category, options) =>
      set((state) => ({
        fabricUsage: {
          ...state.fabricUsage,
          [category]: { ...state.fabricUsage[category], options },
        },
      })),

    getFormData: () => {
      const {
        basicInfo,
        fabricSpecs,
        astar,
        dupatta,
        additionalFabric,
        images,
        fabricUsage,
        notes,
        overallCosting,
      } = get();
      return {
        basicInfo,
        fabricSpecs,
        astar,
        dupatta,
        additionalFabric,
        images,
        fabricUsage,
        notes,
        overallCosting,
      };
    },

    resetBasicInfo: () => set({ basicInfo: initialBasicInfo }),
    resetFabricSpecs: () => set({ fabricSpecs: initialFabricSpecs }),
    resetAstar: () => set({ astar: initialAstar }),
    resetDupatta: () => set({ dupatta: initialDupatta }),
    resetAdditionalFabric: () =>
      set({ additionalFabric: initialAdditionalFabric }),
    resetfabricUsage: () => set({ fabricUsage: initialFabricUsage() }),
    resetImages: () => set({ images: initialImages }),

    resetAll: () =>
      set({
        basicInfo: initialBasicInfo,
        fabricSpecs: initialFabricSpecs,
        astar: initialAstar,
        dupatta: initialDupatta,
        additionalFabric: initialAdditionalFabric,
        images: initialImages,
        fabricUsage: initialFabricUsage(),
        notes: initialNotes,
        overallCosting: initialOverallCosting,
      }),

    validateForm: () => {
      const state = get();
      const missingTabs: Set<string> = new Set();

      Object.entries(requiredFields).forEach(([section, fields]) => {
        fields.forEach((field) => {
          const sectionValue = (state as any)[section];
          const value = sectionValue?.[field];
          if (value === "" || value === null || value === undefined) {
            // yaha tab ka naam push karenge
            missingTabs.add(sectionToTabMap[section]);
          }
        });
      });

      return {
        valid: missingTabs.size === 0,
        missingFields: Array.from(missingTabs),
      };
    },
  })
);
